import React, { useState } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const inputClass =
  'w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10';

const Profile: React.FC = () => {
  const { user, profile, updateProfile, signOut } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
      window.location.href = '/';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    const { error } = await updateProfile(formData);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Profile updated.' });
      setIsEditing(false);
    }
    setLoading(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate min-h-screen w-full bg-white">
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10 mx-auto max-w-2xl px-6 py-14 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <span className="eyebrow">Your account</span>
            <h1 className="mt-2 font-serif text-4xl tracking-tight text-charcoal">Profile</h1>

            <div className="card-frame mt-8 p-8">
              {/* Avatar Section */}
              <div className="flex items-center border-b border-hairline pb-7">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-charcoal text-2xl font-semibold text-white">
                  {profile?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="ml-4 min-w-0">
                  <h2 className="truncate text-xl font-semibold text-charcoal">
                    {profile?.display_name || profile?.username}
                  </h2>
                  <p className="truncate font-mono text-xs text-ink-muted">@{profile?.username}</p>
                </div>
              </div>

              {/* Message */}
              <AnimatePresence>
                {message && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className={`mt-6 overflow-hidden rounded-lg border px-4 py-3 text-sm ${
                      message.type === 'success'
                        ? 'border-black/[0.08] bg-charcoal/[0.04] text-charcoal'
                        : 'border-vermilion/30 bg-vermilion/[0.06] text-vermilion-deep'
                    }`}
                  >
                    {message.text}
                  </motion.div>
                )}
              </AnimatePresence>

              {isEditing ? (
                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  <div>
                    <label
                      htmlFor="display_name"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                    >
                      Display name
                    </label>
                    <input
                      type="text"
                      id="display_name"
                      name="display_name"
                      value={formData.display_name}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="bio"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                    >
                      Bio
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      rows={4}
                      className={`${inputClass} resize-none`}
                      placeholder="Tell us about yourself…"
                    />
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                    >
                      {loading ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          display_name: profile?.display_name || '',
                          bio: profile?.bio || '',
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-7">
                  <dl className="divide-y divide-hairline">
                    {[
                      ['Email', user?.email],
                      ['Display name', profile?.display_name || 'Not set'],
                      ['Bio', profile?.bio || 'No bio yet'],
                    ].map(([label, value]) => (
                      <div key={label} className="grid grid-cols-3 gap-4 py-4 first:pt-0">
                        <dt className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                          {label}
                        </dt>
                        <dd className="col-span-2 break-words text-sm text-charcoal">{value}</dd>
                      </div>
                    ))}
                  </dl>

                  <button onClick={() => setIsEditing(true)} className="btn-primary mt-6">
                    Edit profile
                  </button>
                </div>
              )}
            </div>

            {/* Account actions */}
            <div className="card-frame mt-6 flex items-center justify-between gap-4 p-7">
              <div>
                <h2 className="font-serif text-xl text-charcoal">Account</h2>
                <p className="mt-1 text-sm text-ink-muted">Sign out of Steelman on this device.</p>
              </div>
              <button onClick={handleSignOut} className="btn-secondary flex-shrink-0">
                Sign out
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </MotionConfig>
  );
};

export default Profile;
